import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { ApiRequest } from '@/utils/axios';
import userStore from '@/stores/user.store';
import { modalStore } from '@/stores/modal.store';
import { cryptoStore } from '@/stores/crypto.store';
import { notify } from '@/utils/notify';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import LevelBadge from '@/components/Levels/LevelBadge';
import HoldBalance from '@/components/HoldBalance';
import { getLevelColor } from '@/utils/colors';
import { logout } from '@/utils/auth';
import { rarityColor } from '@/utils/rarity';
import { iconBackgroundStyle } from '@/utils/iconBackground';
import { IUser } from '@/utils/types/User';
import { IJackpotHistoryGame } from '@/utils/types/IJackpotHistoryGame';
import { ICoinflipHistoryGame } from '@/utils/types/ICoinflipGame';
import { ICaseBattleGame } from '@/utils/types/ICaseBattle';
import { IDeposit } from '@/utils/types/IDeposit';
import { IGameItem, IBaseItem } from '@/utils/types/Items';

/* =========================================================
   Shared model — every game tab maps onto the same shape so
   one row renderer covers jackpot / coinflip / case battles.
   ========================================================= */
type Section = 'account' | 'jackpot' | 'coinflip' | 'casebattles' | 'transactions';

interface SkinView {
    icon: string;
    name: string;
    priceUsd: number;
    rarity: string;
    iconBackground: string | null;
}

interface HistoryEntry {
    id: number;
    date: Date | string;
    sum: number;
    win: boolean;
    tag?: string; // small mode/label chip (case battles only)
    skins: SkinView[];
}

const toSkins = (raw: (IGameItem | IBaseItem)[]): SkinView[] =>
    (raw ?? []).filter(Boolean).map((s) => ({
        icon: s.icon,
        name: s.name,
        priceUsd: s.priceUsd,
        rarity: rarityColor(s.colorHex),
        iconBackground: s.iconBackground,
    }));

const MODE_TAG: Record<string, string> = {
    STANDARD_2: '1v1',
    STANDARD_3: '1v1v1',
    STANDARD_4: '4-way',
    STANDARD_5: '5-way',
    STANDARD_6: '6-way',
    TWO_VS_TWO: '2v2',
};

/* =========================================================
   Icons
   ========================================================= */
const ICONS: Record<Section, ReactNode> = {
    account: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
        </svg>
    ),
    jackpot: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
        </svg>
    ),
    coinflip: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="12" rx="5" ry="9" />
            <path d="M12 3v18" />
        </svg>
    ),
    casebattles: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.5 4l5.5 5.5-9 9L5.5 13z" />
            <path d="M3 21l4-1" />
        </svg>
    ),
    transactions: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 7h16M4 7l3-3M20 17H4M20 17l-3 3" />
        </svg>
    ),
};

/* =========================================================
   Root modal
   ========================================================= */
const ProfileModal = () => {
    const { user, statistics, tradeLink, fetchUser } = userStore((s) => s);
    const { data } = modalStore((s) => s);

    const [section, setSection] = useState<Section>((data?.section as Section) || 'account');

    const [jackpots, setJackpots] = useState<IJackpotHistoryGame[]>([]);
    const [coinflips, setCoinflips] = useState<ICoinflipHistoryGame[]>([]);
    const [battles, setBattles] = useState<ICaseBattleGame[]>([]);

    useEffect(() => {
        ApiRequest.get('/jackpot/history/user')
            .then(({ data: { response } }) => setJackpots(response?.history ?? []))
            .catch(() => {});
        ApiRequest.get('/coinflip/history/user')
            .then(({ data: { response } }) => setCoinflips(response?.history ?? []))
            .catch(() => {});
        // history/user → only the current user's battles. (history/all returns
        // the last 20 finished battles globally, so the client-side
        // participation filter below almost always yielded an empty list.)
        ApiRequest.get('/casebattle/history/user')
            .then(({ data: { response } }) => setBattles(response ?? []))
            .catch(() => {});
    }, []);

    /* ---- map raw histories onto HistoryEntry ---- */
    const jackpotEntries: HistoryEntry[] = useMemo(
        () =>
            jackpots.map((g) => ({
                id: g.id,
                date: g.createdAt,
                sum: g.prizeUsd,
                win: g.winnerUser?.id === user.id,
                skins: toSkins(Object.values(g.skins ?? {}).flat() as IGameItem[]),
            })),
        [jackpots, user.id]
    );

    const coinflipEntries: HistoryEntry[] = useMemo(
        () =>
            coinflips.map((g) => ({
                id: g.id,
                date: g.createdAt,
                sum: g.prizeUsd,
                win: g.winnerUser?.id === user.id,
                skins: toSkins(Object.values(g.skins ?? {}).flat() as IGameItem[]),
            })),
        [coinflips, user.id]
    );

    const battleEntries: HistoryEntry[] = useMemo(() => {
        const out: HistoryEntry[] = [];
        for (const g of battles) {
            const team = g.teams?.find((t) =>
                t.participants?.some((p) => p.user?.id === user.id || p.userId === user.id)
            );
            if (!team) continue; // user didn't play this battle
            const me = team.participants.find(
                (p) => p.user?.id === user.id || p.userId === user.id
            );
            const win = team.status === 'WINNER';
            const items = (me?.rolls ?? []).map((r) => r.item);
            out.push({
                id: g.id,
                date: g.createdAt,
                sum: win ? me?.wonAmountUsd ?? team.amountUsd : me?.rollsAmountUsd ?? 0,
                win,
                tag: MODE_TAG[g.type] ?? undefined,
                skins: toSkins(items),
            });
        }
        return out;
    }, [battles, user.id]);

    const counts = {
        jackpot: jackpotEntries.length,
        coinflip: coinflipEntries.length,
        casebattles: battleEntries.length,
    };

    return (
        <div className="w-[min(1180px,100%)] flex gap-5 max-md:flex-col h-[min(640px,calc(100vh-150px))] max-md:h-auto">
            {/* ---- section rail ---- */}
            <nav className="flex flex-col gap-1.5 w-[208px] shrink-0 max-md:w-full max-md:flex-row max-md:overflow-x-auto max-md:pb-1 no-scrollbar">
                {(
                    [
                        ['account', 'Account'],
                        ['jackpot', 'Jackpot'],
                        ['coinflip', 'Coinflip'],
                        ['casebattles', 'Case Battles'],
                        ['transactions', 'Transactions'],
                    ] as [Section, string][]
                ).map(([id, label]) => (
                    <NavItem
                        key={id}
                        active={section === id}
                        onClick={() => setSection(id)}
                        icon={ICONS[id]}
                        label={label}
                        count={(counts as Record<string, number>)[id]}
                    />
                ))}
            </nav>

            {/* ---- content ---- */}
            <div className="flex-1 min-w-0 overflow-y-auto pr-1 -mr-1 fluent-scrollbar max-md:overflow-visible">
                {section === 'account' && (
                    <AccountSection
                        user={user}
                        deposited={
                            jackpotEntries.length + coinflipEntries.length + battleEntries.length
                        }
                        won={
                            jackpotEntries.filter((e) => e.win).length +
                            coinflipEntries.filter((e) => e.win).length +
                            battleEntries.filter((e) => e.win).length
                        }
                        profit={
                            (statistics?.jackpot?.valueUsd ?? 0) +
                            (statistics?.coinflip?.valueUsd ?? 0)
                        }
                        initialTradeLink={tradeLink()}
                        fetchUser={fetchUser}
                        highlightTradeLink={!!data?.highlightTradeLink}
                    />
                )}
                {section === 'jackpot' && (
                    <HistorySection entries={jackpotEntries} empty="No jackpot games yet" accent="#ffc24d" />
                )}
                {section === 'coinflip' && (
                    <HistorySection entries={coinflipEntries} empty="No coinflip games yet" accent="#46e08a" />
                )}
                {section === 'casebattles' && (
                    <HistorySection entries={battleEntries} empty="No case battles yet" accent="#8b6dff" />
                )}
                {section === 'transactions' && <TransactionsSection />}
            </div>
        </div>
    );
};

/* =========================================================
   Section rail item
   ========================================================= */
const NavItem = ({
    active,
    onClick,
    icon,
    label,
    count,
}: {
    active: boolean;
    onClick: () => void;
    icon: ReactNode;
    label: string;
    count?: number;
}) => (
    <button
        onClick={onClick}
        className={`group relative font-chat-nick flex items-center gap-3 px-3.5 py-3 rounded-fl text-[13px] font-bold tracking-[0.04em] transition-all max-md:shrink-0 max-md:whitespace-nowrap ${
            active
                ? 'text-white'
                : 'text-text_3 bg-layer_2 border border-stroke hover:text-text_main hover:border-stroke_strong hover:bg-layer_3'
        }`}
        style={
            active
                ? {
                      background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                      boxShadow:
                          'inset 0 1px 0 rgba(255,255,255,0.3), 0 0 22px -6px rgba(139,109,255,0.8), 0 8px 18px -8px rgba(58,214,255,0.6)',
                  }
                : undefined
        }
    >
        <span className="w-4 h-4 shrink-0">{icon}</span>
        <span className="flex-1 text-left">{label}</span>
        {typeof count === 'number' && count > 0 && (
            <span
                className={`text-[10.5px] px-1.5 min-w-[20px] h-[18px] grid place-items-center rounded-full tabular-nums ${
                    active ? 'bg-black/25 text-white' : 'bg-layer_3 text-text_2'
                }`}
            >
                {count}
            </span>
        )}
    </button>
);

/* =========================================================
   ACCOUNT — regrouped: hero strip · stat trio · settings
   ========================================================= */
const AccountSection = ({
    user,
    deposited,
    won,
    profit,
    initialTradeLink,
    fetchUser,
    highlightTradeLink,
}: {
    user: IUser;
    deposited: number;
    won: number;
    profit: number;
    initialTradeLink: string;
    fetchUser: () => void;
    highlightTradeLink?: boolean;
}) => {
    const lvl = Math.max(0, Math.floor((user.experience ?? 0) / 100));
    const pct = Math.min(100, Math.max(0, (((user.experience ?? 0) % 100) + 100) % 100));
    const lvlColor = getLevelColor(lvl);

    return (
        <div className="flex flex-col gap-3.5">
            {/* hero */}
            <div className="surface relative overflow-hidden p-5 max-md:p-4 flex flex-col gap-4">
                <div
                    aria-hidden
                    className="absolute -top-16 -right-10 w-56 h-56 rounded-full pointer-events-none opacity-50"
                    style={{ background: `radial-gradient(circle, ${lvlColor}22, transparent 70%)` }}
                />
                <div className="relative flex items-center gap-4 max-md:flex-col max-md:items-start">
                    <div className="relative w-[78px] h-[78px] shrink-0">
                        <AbstractAvatar
                            square
                            seed={user.name || String(user.id)}
                            src={user.avatar}
                            size={78}
                            className="!w-full !h-full"
                        />
                        <span
                            className="absolute -inset-[3px] rounded-[30%] pointer-events-none"
                            style={{ border: `2px solid ${lvlColor}`, boxShadow: `0 0 18px -3px ${lvlColor}` }}
                        />
                        <span className="absolute -bottom-1.5 -right-1.5">
                            <LevelBadge level={lvl} color={lvlColor} variant="solid" size="md" />
                        </span>
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                        <div className="font-chat-nick text-[22px] font-bold text-text_main truncate leading-none">
                            {user.name || '—'}
                        </div>
                        <div className="flex items-center gap-2.5 flex-wrap">
                            <span
                                className="font-chat-nick text-[20px] font-extrabold text-accent_5 tabular-nums leading-none"
                                style={{ textShadow: '0 0 14px rgba(255,194,77,0.32)' }}
                            >
                                ${(user.balanceUsd ?? 0).toFixed(2)}
                            </span>
                            <BrandLink href="/deposit" tone="money" small>
                                <svg viewBox="0 0 24 24" className="relative w-3 h-3 stroke-current fill-none [stroke-width:2.6] [stroke-linecap:round]">
                                    <path d="M12 5v14M5 12h14" />
                                </svg>
                                ADD FUNDS
                            </BrandLink>
                        </div>
                        {/* Funds on hold — shown under the balance when > 0 */}
                        <HoldBalance variant="inline" />
                    </div>
                </div>

                {/* level progress */}
                <div className="relative flex flex-col gap-1.5">
                    <div className="flex items-center justify-between font-chat-nick text-[11px] tracking-[0.04em]">
                        <span className="text-text_3">
                            LEVEL <b className="text-text_main">{lvl}</b>
                        </span>
                        <span className="text-accent tabular-nums">{pct}% → LVL {lvl + 1}</span>
                    </div>
                    <div className="h-2.5 rounded-md overflow-hidden relative" style={{ background: 'rgba(139,109,255,0.16)' }}>
                        <div
                            className="absolute inset-y-0 left-0 rounded-md transition-[width] duration-500"
                            style={{
                                width: `${pct}%`,
                                background: 'linear-gradient(90deg, #8b6dff, #3ad6ff)',
                                boxShadow: '0 0 12px rgba(58,214,255,0.45)',
                            }}
                        >
                            <span
                                aria-hidden
                                className="absolute inset-0"
                                style={{
                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)',
                                    animation: 'lvlShine 2.5s linear infinite',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* stat trio */}
            <div className="grid grid-cols-3 gap-3 max-md:grid-cols-1">
                <StatTile label="Games deposited" value={deposited} color="#3ad6ff" />
                <StatTile label="Games won" value={won} color="#46e08a" />
                <ProfitTile profit={profit} />
            </div>

            {/* settings */}
            <AccountSettings
                initialTradeLink={initialTradeLink}
                fetchUser={fetchUser}
                highlightTradeLink={highlightTradeLink}
            />
        </div>
    );
};

const StatTile = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <div
        className="surface relative overflow-hidden p-4 flex flex-col gap-1"
        style={{ borderColor: `color-mix(in srgb, ${color} 24%, rgba(150,130,255,0.16))` }}
    >
        <span
            aria-hidden
            className="absolute top-0 left-0 h-full w-1"
            style={{ background: `linear-gradient(180deg, ${color}, transparent)` }}
        />
        <span className="font-chat-nick text-[10.5px] uppercase tracking-[0.12em] text-text_3 font-bold">
            {label}
        </span>
        <span className="font-chat-nick text-[28px] font-extrabold text-text_main tabular-nums leading-none mt-0.5">
            {value}
        </span>
    </div>
);

const ProfitTile = ({ profit }: { profit: number }) => (
    <div
        className="surface relative overflow-hidden p-4 flex items-center justify-between gap-2"
        style={{
            background:
                'linear-gradient(135deg, rgba(255,194,77,0.14), rgba(255,194,77,0.02) 60%), rgba(139,109,255,0.06)',
            border: '1px solid color-mix(in srgb, #ffc24d 26%, rgba(150,130,255,0.16))',
        }}
    >
        <div className="min-w-0">
            <span className="font-chat-nick text-[10.5px] uppercase tracking-[0.12em] text-text_3 font-bold">
                Profit
            </span>
            <div
                className="font-chat-nick text-[24px] font-extrabold text-accent_5 tabular-nums leading-none mt-1"
                style={{ textShadow: '0 0 16px rgba(255,194,77,0.32)' }}
            >
                ${profit.toFixed(2)}
            </div>
        </div>
        <BrandLink href="/withdraw" tone="money">
            <svg viewBox="0 0 24 24" className="relative w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round] [stroke-linejoin:round]">
                <path d="M12 19V5M5 12l7-7 7 7" />
            </svg>
            CASH OUT
        </BrandLink>
    </div>
);

/* ---- account settings (trade link + promocode) ---- */
const AccountSettings = ({
    initialTradeLink,
    fetchUser,
    highlightTradeLink,
}: {
    initialTradeLink: string;
    fetchUser: () => void;
    highlightTradeLink?: boolean;
}) => {
    const [tradeLink, setTradeLink] = useState(initialTradeLink ?? '');
    const [tradeBusy, setTradeBusy] = useState(false);
    const [promo, setPromo] = useState('');
    const [promoBusy, setPromoBusy] = useState(false);
    const tradeInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => setTradeLink(initialTradeLink ?? ''), [initialTradeLink]);

    // When the modal is opened specifically to make the user fill in their
    // trade link, draw attention to the field.
    const needTradeLink = !!highlightTradeLink && !tradeLink.trim();
    useEffect(() => {
        if (!highlightTradeLink) return;
        const t = setTimeout(() => {
            tradeInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            tradeInputRef.current?.focus();
        }, 120);
        return () => clearTimeout(t);
    }, [highlightTradeLink]);

    async function applyTradeLink() {
        if (!tradeLink.trim()) {
            notify('error', <AlertIcon />, <NotifyWrapper title="Error" text="Paste your trade link" />);
            return;
        }
        setTradeBusy(true);
        try {
            const res = await ApiRequest.post('/user/tradelink/save', { tradelink: tradeLink });
            if (res) {
                fetchUser();
                notify('success', <SuccessIcon />, <NotifyWrapper title="Success" text="Trade link updated" />);
            }
        } finally {
            setTradeBusy(false);
        }
    }

    async function applyPromo() {
        if (!promo.trim()) {
            notify('error', <AlertIcon />, <NotifyWrapper title="Error" text="Paste a promocode first" />);
            return;
        }
        setPromoBusy(true);
        try {
            const res = await ApiRequest.post('/promocodes/activate', { code: promo });
            if (res?.data?.success) {
                fetchUser();
                setPromo('');
                notify('success', <SuccessIcon />, <NotifyWrapper title="Success" text="Promo activated — balance updated!" />, 3000);
            }
        } finally {
            setPromoBusy(false);
        }
    }

    return (
        <div className="surface p-5 max-md:p-4 flex flex-col gap-4">
            <span className="font-chat-nick flex items-center gap-2.5 text-[13px] font-bold text-text_main uppercase tracking-[0.06em]">
                <span aria-hidden className="w-1 h-4 rounded-full" style={{ background: 'linear-gradient(180deg, #8b6dff, #3ad6ff)', boxShadow: '0 0 12px -2px rgba(58,214,255,0.7)' }} />
                Account settings
            </span>

            <div className="flex flex-col gap-1.5">
                <Field label="Steam trade link">
                    <input
                        ref={tradeInputRef}
                        type="text"
                        value={tradeLink}
                        onChange={(e) => setTradeLink(e.target.value)}
                        placeholder="https://steamcommunity.com/tradeoffer/new/?partner=…&token=…"
                        className={`flex-1 min-w-0 rounded-fl px-3.5 py-2.5 text-[12.5px] text-text_main font-mono outline-none transition-all placeholder:text-text_4 ${
                            needTradeLink
                                ? 'bg-[rgba(255,98,98,0.08)] border border-danger shadow-[0_0_0_3px_rgba(255,98,98,0.18)] focus:border-danger'
                                : 'bg-layer_2 border border-stroke focus:border-accent focus:bg-layer_3 focus:shadow-[0_0_0_3px_rgba(58,214,255,0.16)]'
                        }`}
                    />
                    <ApplyButton onClick={applyTradeLink} busy={tradeBusy} />
                    <button
                        onClick={() =>
                            window.open(
                                'https://steamcommunity.com/my/tradeoffers/privacy#trade_offer_access_url',
                                '_blank'
                            )
                        }
                        className="font-chat-nick px-4 py-2.5 rounded-fl bg-layer_2 border border-stroke text-text_2 text-xs font-bold tracking-[0.06em] whitespace-nowrap hover:bg-layer_3 hover:border-stroke_strong hover:text-text_main transition-colors"
                    >
                        GET LINK
                    </button>
                </Field>
                {needTradeLink && (
                    <span className="font-chat-msg flex items-center gap-1.5 text-[12px] font-semibold text-danger pl-0.5">
                        <svg
                            viewBox="0 0 24 24"
                            className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round] [stroke-linejoin:round]"
                        >
                            <path d="M12 8v5M12 16v.5" />
                            <circle cx="12" cy="12" r="9.5" />
                        </svg>
                        Need put tradelink!
                    </span>
                )}
            </div>

            <Field label="Promocode">
                <input
                    type="text"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    placeholder="Paste promocode here"
                    className="flex-1 min-w-0 bg-layer_2 border border-stroke focus:border-accent focus:bg-layer_3 focus:shadow-[0_0_0_3px_rgba(58,214,255,0.16)] rounded-fl px-3.5 py-2.5 text-[13px] text-text_main outline-none transition-all placeholder:text-text_4"
                />
                <ApplyButton onClick={applyPromo} busy={promoBusy} />
            </Field>

            <button
                onClick={() => logout()}
                className="font-chat-nick mt-1 inline-flex items-center justify-center gap-2 self-start px-4 py-2.5 rounded-fl bg-layer_2 border border-stroke text-text_3 text-[12px] font-extrabold tracking-[0.07em] uppercase hover:bg-[rgba(255,98,98,0.12)] hover:border-[rgba(255,98,98,0.5)] hover:text-danger transition-colors"
            >
                <svg
                    viewBox="0 0 24 24"
                    className="w-4 h-4 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                >
                    <path d="M15 17l5-5-5-5M20 12H9M12 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7" />
                </svg>
                Log out
            </button>
        </div>
    );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
    <label className="flex flex-col gap-1.5">
        <span className="font-chat-nick text-[11px] uppercase tracking-[0.10em] text-text_3 font-bold">
            {label}
        </span>
        <div className="flex items-stretch gap-2 max-md:flex-col">{children}</div>
    </label>
);

const ApplyButton = ({ onClick, busy }: { onClick: () => void; busy: boolean }) => (
    <button
        onClick={onClick}
        disabled={busy}
        className="group relative overflow-hidden font-chat-nick px-5 py-2.5 rounded-fl text-white text-xs font-extrabold tracking-[0.08em] whitespace-nowrap hover:-translate-y-0.5 transition-transform disabled:opacity-50 disabled:hover:translate-y-0"
        style={{
            background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
            boxShadow:
                'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 22px -4px rgba(139,109,255,0.7), 0 8px 20px -6px rgba(58,214,255,0.5)',
        }}
    >
        <span
            aria-hidden
            className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
            style={{ background: 'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)' }}
        />
        <span className="relative">APPLY</span>
    </button>
);

/* =========================================================
   GAME HISTORY (jackpot / coinflip / case battles)
   ========================================================= */
const HistorySection = ({
    entries,
    empty,
    accent,
}: {
    entries: HistoryEntry[];
    empty: string;
    accent: string;
}) => {
    if (entries.length === 0) return <EmptyState text={empty} />;
    return (
        <div className="flex flex-col gap-2.5">
            {entries.map((e) => (
                <HistoryCard key={e.id} entry={e} accent={accent} />
            ))}
        </div>
    );
};

const HistoryCard = ({ entry, accent }: { entry: HistoryEntry; accent: string }) => {
    const date = new Date(entry.date);
    return (
        <div
            className="bg-layer_1 border border-stroke rounded-fl-md p-3.5 flex items-center gap-4 transition-colors hover:bg-layer_2 hover:border-stroke_strong max-md:flex-col max-md:items-stretch"
            style={{ animation: 'pfRowIn 0.4s cubic-bezier(0.18,0.84,0.30,1) both' }}
        >
            {/* left identity block */}
            <div className="flex items-center gap-3 shrink-0 w-[208px] max-md:w-full">
                <span
                    className="font-chat-nick inline-flex items-center justify-center h-11 min-w-[44px] px-2.5 rounded-[12px] text-[12px] font-extrabold tabular-nums shrink-0 leading-none"
                    style={{
                        color: accent,
                        background: `color-mix(in srgb, ${accent} 12%, transparent)`,
                        border: `1px solid color-mix(in srgb, ${accent} 32%, transparent)`,
                        boxShadow: `0 0 16px -6px ${accent}`,
                    }}
                >
                    #{entry.id}
                </span>
                <div className="flex flex-col gap-1 min-w-0">
                    <span
                        className={`font-chat-nick text-[11px] font-extrabold tracking-[0.10em] px-2 py-0.5 rounded-[6px] w-fit ${
                            entry.win ? 'pf-status-win' : 'pf-status-lose'
                        }`}
                    >
                        {entry.win ? 'WIN' : 'LOSE'}
                    </span>
                    <span className="font-chat-nick text-[11.5px] text-text_3 tabular-nums flex items-center gap-1.5">
                        {date.toLocaleDateString()}
                        {entry.tag && (
                            <span className="px-1.5 py-px rounded-[5px] bg-layer_2 border border-stroke text-text_2 text-[10px] tracking-[0.06em]">
                                {entry.tag}
                            </span>
                        )}
                    </span>
                </div>
            </div>

            {/* skins strip */}
            <div className="flex-1 min-w-0">
                <SkinStrip skins={entry.skins} />
            </div>

            {/* amount */}
            <span
                className="font-chat-nick text-[16px] font-bold tabular-nums px-3.5 py-2 rounded-[10px] shrink-0 text-right max-md:self-end"
                style={{
                    color: '#ffc24d',
                    border: '1px solid color-mix(in srgb, #ffc24d 38%, transparent)',
                    background: 'color-mix(in srgb, #ffc24d 10%, transparent)',
                    textShadow: '0 0 14px rgba(255,194,77,0.5)',
                }}
            >
                ${entry.sum.toFixed(2)}
            </span>
        </div>
    );
};

const SkinStrip = ({ skins }: { skins: SkinView[] }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    if (skins.length === 0) return <span className="text-xs text-text_4 italic">No skins</span>;
    return (
        <div
            ref={trackRef}
            className="flex gap-2 overflow-x-auto overflow-y-hidden py-1 fluent-scrollbar"
            style={{ scrollBehavior: 'smooth' }}
        >
            {skins.map((s, i) => (
                <div
                    key={i}
                    data-tip="1"
                    data-name={s.name}
                    data-price={s.priceUsd}
                    data-rarity={s.rarity}
                    data-image={s.icon}
                    data-icon-background={s.iconBackground ?? ''}
                    style={{ ['--rarity' as string]: s.rarity } as React.CSSProperties}
                    className="relative w-12 h-12 rounded-[12px] bg-layer_2 border border-stroke grid place-items-center shrink-0 cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:bg-white/10 hover:[border-color:var(--rarity)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.35),0_0_14px_-2px_var(--rarity)]"
                >
                    <span
                        className="absolute top-1 left-1 w-1.5 h-1.5 rounded-full pointer-events-none"
                        style={{ background: s.rarity, boxShadow: `0 0 5px ${s.rarity}` }}
                    />
                    {s.icon ? (
                        <img
                            src={s.icon}
                            alt=""
                            draggable={false}
                            className="w-8 h-8 object-contain pointer-events-none"
                            style={iconBackgroundStyle(s.iconBackground)}
                        />
                    ) : (
                        <div className="w-8 h-8 bg-layer_3 rounded" />
                    )}
                </div>
            ))}
        </div>
    );
};

/* =========================================================
   TRANSACTIONS — crypto deposits
   ========================================================= */
const TransactionsSection = () => {
    const { getWallet, getDeposits } = cryptoStore((s) => s);
    const { toggleModal, setTitle, setComponent, setData } = modalStore((s) => s);
    const [deposits, setDeposits] = useState<IDeposit[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        getDeposits()
            .then((d) => {
                if (cancelled) return;
                setDeposits(
                    d.slice().sort(
                        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                    )
                );
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function view(d: IDeposit) {
        const wallet = getWallet(d.walletId);
        if (!wallet) return;
        setTitle(`DEPOSIT ${wallet.coinName}`);
        setComponent('Crypto address');
        setData({
            currency: wallet.coinName,
            value: d.amountUsd,
            wallet,
            status: d.status,
            id: d.id,
            back: {
                title: 'PROFILE',
                component: 'Profile',
                data: { modalSize: 'lg', section: 'transactions' },
            },
        });
        toggleModal(true);
    }

    if (loading) return <EmptyState text="Loading…" />;
    if (deposits.length === 0) return <EmptyState text="No crypto deposits yet" />;

    return (
        <div className="flex flex-col gap-2.5">
            {deposits.map((d) => {
                const wallet = getWallet(d.walletId);
                if (!wallet) return null;
                const statusCls =
                    d.status === 'COMPLETED'
                        ? 'pf-status-win'
                        : d.status === 'DECLINED'
                          ? 'pf-status-lose'
                          : 'pf-status-pending';
                return (
                    <div
                        key={d.id}
                        className="bg-layer_1 border border-stroke rounded-fl-md p-3.5 flex items-center gap-4 transition-colors hover:bg-layer_2 hover:border-stroke_strong max-md:flex-wrap"
                        style={{ animation: 'pfRowIn 0.4s cubic-bezier(0.18,0.84,0.30,1) both' }}
                    >
                        <span className="font-chat-nick grid place-items-center w-14 h-11 rounded-[12px] text-[12px] font-bold text-text_2 bg-layer_2 border border-stroke shrink-0">
                            {wallet.coinName}
                        </span>
                        <span className={`font-chat-nick text-[11px] font-extrabold tracking-[0.10em] px-2.5 py-1 rounded-[6px] shrink-0 ${statusCls}`}>
                            {d.status}
                        </span>
                        <span className="font-chat-nick text-[12px] text-text_3 tabular-nums shrink-0">
                            {new Date(d.createdAt).toLocaleDateString()}
                        </span>
                        <span className="font-chat-nick flex-1 min-w-0 text-right text-sm font-bold text-accent_5 tabular-nums max-md:w-full">
                            {typeof d.amountCoin === 'number'
                                ? d.amountCoin.toFixed(8).replace(/\.?0+$/, '')
                                : '—'}{' '}
                            <span className="opacity-70">(${d.amountUsd})</span>
                        </span>
                        <button
                            onClick={() => view(d)}
                            className="group relative overflow-hidden font-chat-nick px-4 py-2 rounded-fl text-white text-xs font-extrabold tracking-[0.08em] shrink-0 hover:-translate-y-0.5 transition-transform max-md:w-full"
                            style={{
                                background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                                boxShadow:
                                    'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 18px -4px rgba(139,109,255,0.7), 0 6px 16px -6px rgba(58,214,255,0.5)',
                            }}
                        >
                            <span
                                aria-hidden
                                className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                                style={{ background: 'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)' }}
                            />
                            <span className="relative">VIEW</span>
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

/* =========================================================
   Small shared bits
   ========================================================= */
const EmptyState = ({ text }: { text: string }) => (
    <div className="h-full min-h-[200px] grid place-items-center text-center text-text_3 text-sm border border-dashed border-stroke rounded-fl-md py-16 px-6">
        {text}
    </div>
);

const BrandLink = ({
    href,
    children,
    tone,
    small,
}: {
    href: string;
    children: ReactNode;
    tone: 'money' | 'brand';
    small?: boolean;
}) => (
    <Link
        href={href}
        onClick={() => modalStore.getState().close()}
        className={`group relative overflow-hidden font-chat-nick inline-flex items-center gap-1.5 rounded-fl font-extrabold tracking-[0.06em] hover:-translate-y-0.5 transition-transform ${
            small ? 'px-3.5 py-1.5 text-[11px]' : 'px-4 py-2.5 text-[12.5px]'
        } ${tone === 'money' ? 'text-[#06121b]' : 'text-white'}`}
        style={{
            background:
                tone === 'money'
                    ? 'linear-gradient(135deg, #46e08a, #3ad6ff)'
                    : 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
            boxShadow:
                tone === 'money'
                    ? 'inset 0 1px 0 rgba(255,255,255,0.4), 0 0 20px -4px rgba(70,224,138,0.65), 0 6px 16px -6px rgba(58,214,255,0.5)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 20px -4px rgba(139,109,255,0.7), 0 6px 16px -6px rgba(58,214,255,0.5)',
        }}
    >
        <span
            aria-hidden
            className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
            style={{ background: 'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)' }}
        />
        <span className="relative inline-flex items-center gap-1.5">{children}</span>
    </Link>
);

export default ProfileModal;
